import React, { useState, useMemo } from 'react';
import { type Customer, type Deposit } from '../store/customerTypes';
import { Table, Modal, Button, Spin } from 'antd';
import PlusFilledWhite from '../../../assets/icons/plus-filled-white.svg';
import DepositPaymentModal from '../../Deposit/DepositPayment';
import DepositCloseModal from '../../Deposit/DepositClose';
import DepositTransactionsModal from '../../Deposit/DepositTransactions';
import DepositReceiptPrintable from '../../Deposit/DepositReceiptPrintable';
import { type DepositResponse, type DepositReceiptData } from '../../Deposit/store/depositTypes';
import { getDepositReceiptAction } from '../../Deposit/store/depositActions';
import { useDispatch } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import dayjs from 'dayjs';
import fmt from 'indian-number-format';

interface Props {
  deposits?: Deposit[];
  customer?: Customer;
  refetchCustomer: (customerId: string) => void;
}

export default function DepositDetailsIndex({
  deposits,
  customer,
  refetchCustomer,
}: Props): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedDeposit, setSelectedDeposit] = useState<Deposit>();
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isTransactionsModalOpen, setIsTransactionsModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<DepositReceiptData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const showPayModal = () => {
    setIsPayModalOpen(true);
  };

  const showCloseModal = () => {
    setIsCloseModalOpen(true);
  };

  const handlePayClose = () => {
    setIsPayModalOpen(false);
    if (customer?.customerId != null && customer.customerId !== '') {
      refetchCustomer(String(customer.customerId));
    }
  };

  const handleCloseModalClose = () => {
    setIsCloseModalOpen(false);
    if (customer?.customerId != null && customer.customerId !== '') {
      refetchCustomer(String(customer.customerId));
    }
  };

  const handleTransactionsModalClose = () => {
    setIsTransactionsModalOpen(false);
  };

  const handlePrint = async (record: Deposit) => {
    if (record.depositNumber === undefined) return;
    setIsLoading(true);
    const response = await getDepositReceiptAction(dispatch, record.depositNumber);
    setIsLoading(false);
    if (response?.receipt !== undefined) {
      setReceiptData(response.receipt);
      setIsPrintModalOpen(true);
    }
  };

  const handlePrintModalClose = () => {
    setIsPrintModalOpen(false);
    setReceiptData(null);
  };

  // Filter deposits into current and closed
  const currentDeposits = useMemo(() => {
    return (
      deposits?.filter((deposit: Deposit) => deposit.status !== 'closed') ?? []
    );
  }, [deposits]);

  const closedDeposits = useMemo(() => {
    return (
      deposits?.filter((deposit: Deposit) => deposit.status === 'closed') ?? []
    );
  }, [deposits]);

  const depositColumns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: Deposit, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Deposit Number',
      dataIndex: 'depositNumber',
      key: 'depositNumber',
      render: (_: any, record: Deposit) => <p>{record.depositNumber}</p>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (_: any, record: Deposit) => <p>{fmt.format(record.amount)}</p>,
    },
    {
      title: 'Nominee',
      dataIndex: 'nominee',
      key: 'nominee',
      render: (_: any, record: Deposit) => <p>{record.nominee}</p>,
    },
    {
      title: 'Pending Interest',
      dataIndex: 'pendingInterest',
      key: 'pendingInterest',
      render: (_: any, record: Deposit) => (
        <p>
          {fmt.format(record.pendingInterest)} ({record.interestDays} days) @{' '}
          {record.interest}%
        </p>
      ),
    },
    {
      title: 'Invested Date',
      dataIndex: 'investedDate',
      key: 'investedDate',
      render: (_: any, record: Deposit) => (
        <p>{dayjs(String(record.investedDate)).format('DD-MM-YYYY')}</p>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: 320,
      render: (_: any, record: Deposit) => {
        const canClose = (record.pendingInterest ?? 0) === 0;
        const canPay = (record.pendingInterest ?? 0) > 0;
        return (
          <div style={{ display: 'flex', gap: 8 }}>
            {canPay && (
              <p
                style={{
                  width: 73,
                  height: 34,
                  borderRadius: 12,
                  backgroundColor: '#DD1E2D',
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => {
                  setSelectedDeposit(record);
                  showPayModal();
                }}
              >
                Pay
              </p>
            )}
            {canClose && (
              <p
                style={{
                  width: 73,
                  height: 34,
                  borderRadius: 12,
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => {
                  setSelectedDeposit(record);
                  showCloseModal();
                }}
              >
                Close
              </p>
            )}
            <p
              style={{
                width: 73,
                height: 34,
                borderRadius: 12,
                backgroundColor: '#DD1E2D',
                color: 'white',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => {
                setSelectedDeposit(record);
                setIsTransactionsModalOpen(true);
              }}
            >
              View
            </p>
            <p
              style={{
                width: 73,
                height: 34,
                borderRadius: 12,
                backgroundColor: '#DD1E2D',
                color: 'white',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => {
                void handlePrint(record);
              }}
            >
              Print
            </p>
          </div>
        );
      },
    },
  ];

  const closedColumns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: Deposit, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Deposit Number',
      dataIndex: 'depositNumber',
      key: 'depositNumber',
      render: (_: any, record: Deposit) => <p>{record.depositNumber}</p>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (_: any, record: Deposit) => <p>{fmt.format(record.amount)}</p>,
    },
    {
      title: 'Nominee',
      dataIndex: 'nominee',
      key: 'nominee',
      render: (_: any, record: Deposit) => <p>{record.nominee}</p>,
    },
    {
      title: 'Invested Date',
      dataIndex: 'investedDate',
      key: 'investedDate',
      render: (_: any, record: Deposit) => (
        <p>{dayjs(String(record.investedDate)).format('DD-MM-YYYY')}</p>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: 100,
      render: (_: any, record: Deposit) => (
        <p
          style={{
            width: 73,
            height: 34,
            borderRadius: 12,
            backgroundColor: '#DD1E2D',
            color: 'white',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => {
            setSelectedDeposit(record);
            setIsTransactionsModalOpen(true);
          }}
        >
          View
        </p>
      ),
    },
  ];

  return (
    <div className="loan__details__page">
      <Spin spinning={isLoading} fullscreen />
      <div className="closed__loan__section">
        <div className="current__loan__header">
          <p className="closed__loan__title">Current Deposits</p>
          <a
            className="create__loan__button"
            href={`/deposit?customerId=${customer?.customerId}`}
          >
            <img className="plus__icon" src={PlusFilledWhite} /> Create Deposit
          </a>
        </div>

        <Table
          dataSource={currentDeposits}
          columns={depositColumns}
          pagination={false}
          rowKey={(record: Deposit) => record._id}
        />
      </div>

      <div className="closed__loan__section" style={{ marginTop: 24 }}>
        <div className="current__loan__header">
          <p className="closed__loan__title">Closed Deposits</p>
        </div>

        <Table
          dataSource={closedDeposits}
          columns={closedColumns}
          pagination={false}
          rowKey={(record: Deposit) => record._id}
        />
      </div>
      <Modal
        width={727}
        title={`Pay Deposit - ${customer?.name ?? ''}`}
        open={isPayModalOpen}
        onCancel={handlePayClose}
        destroyOnClose
        footer={() => <></>}
      >
        {selectedDeposit !== undefined && (
          <DepositPaymentModal
            closeModalNOOP={handlePayClose}
            depositDetail={
              {
                id: selectedDeposit._id,
                amount: selectedDeposit.amount,
                branch:
                  typeof selectedDeposit.branch === 'string'
                    ? selectedDeposit.branch
                    : selectedDeposit.branch._id,
                interest: String(selectedDeposit.interest),
                nominee: selectedDeposit.nominee,
                investedDate: selectedDeposit.investedDate,
                customerName: selectedDeposit.customerName,
                pendingInterest: selectedDeposit.pendingInterest,
                interestDays: selectedDeposit.interestDays,
                perDayInterest: selectedDeposit.perDayInterest,
                remainingPrincipal: selectedDeposit.remainingPrincipal,
                lastPaymentDate: selectedDeposit.lastPaymentDate,
                depositNumber: selectedDeposit.depositNumber,
              } satisfies DepositResponse
            }
          />
        )}
      </Modal>
      <Modal
        width={727}
        title={`Close Deposit - ${customer?.name ?? ''}`}
        open={isCloseModalOpen}
        onCancel={handleCloseModalClose}
        destroyOnClose
        footer={() => <></>}
      >
        {selectedDeposit !== undefined && (
          <DepositCloseModal
            closeModalNOOP={handleCloseModalClose}
            depositDetail={
              {
                id: selectedDeposit._id,
                amount: selectedDeposit.amount,
                branch:
                  typeof selectedDeposit.branch === 'string'
                    ? selectedDeposit.branch
                    : selectedDeposit.branch._id,
                interest: String(selectedDeposit.interest),
                nominee: selectedDeposit.nominee,
                investedDate: selectedDeposit.investedDate,
                customerName: selectedDeposit.customerName,
                pendingInterest: selectedDeposit.pendingInterest,
                interestDays: selectedDeposit.interestDays,
                perDayInterest: selectedDeposit.perDayInterest,
                remainingPrincipal: selectedDeposit.remainingPrincipal,
                lastPaymentDate: selectedDeposit.lastPaymentDate,
                depositNumber: selectedDeposit.depositNumber,
              } satisfies DepositResponse
            }
          />
        )}
      </Modal>
      <Modal
        width={900}
        title={`Deposit Transactions - ${customer?.name ?? ''}`}
        open={isTransactionsModalOpen}
        onCancel={handleTransactionsModalClose}
        destroyOnClose
        footer={() => <></>}
      >
        {selectedDeposit !== undefined && (
          <DepositTransactionsModal
            transactions={selectedDeposit.transactions}
            depositNumber={selectedDeposit.depositNumber}
          />
        )}
      </Modal>
      <Modal
        width={800}
        title="Deposit Receipt"
        open={isPrintModalOpen}
        onCancel={handlePrintModalClose}
        destroyOnClose
        footer={() => (
          <Button onClick={handlePrintModalClose}>Close</Button>
        )}
      >
        {receiptData !== null && (
          <DepositReceiptPrintable receipt={receiptData} />
        )}
      </Modal>
    </div>
  );
}
